import { useTranslation } from "react-i18next";
import { Download, Paperclip, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { proofDataUrl } from "../lib/claimRules";
import { borderFor } from "./formControls";

/**
 * The proofs control shared by every ESS claim form — per-km trip lines, fixed
 * monthly and vehicle maintenance.
 *
 * Two shapes of attachment live side by side in the same list, and both behave
 * identically here:
 *
 *   - a File the user just picked, not yet encoded
 *   - a document already stored on the claim, rehydrated as { name, data },
 *     where `data` is the Base64 the API returned
 *
 * Every chip can therefore be downloaded whether the proof came from disk a
 * second ago or from the claim months ago, and removed the same way. Removal
 * is state only: the claim is saved whole, so a proof dropped here is simply
 * absent from the next save and the stored document goes with it.
 */

/** The href a chip downloads from, plus whether it must be revoked after. */
function downloadTarget(proof) {
  if (typeof proof?.data === "string") return { url: proofDataUrl(proof.data), temporary: false };
  if (typeof Blob !== "undefined" && proof instanceof Blob) {
    return { url: URL.createObjectURL(proof), temporary: true };
  }
  return null;
}

export function ProofAttachments({
  proofs = [],
  onChange,
  disabled = false,
  readOnly = false,
  error,
  accept = "image/*,application/pdf",
  multiple = true,
}) {
  const { t } = useTranslation();

  const add = (event) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length) onChange([...(proofs || []), ...picked]);
    // Cleared so re-picking the same file still fires a change.
    event.target.value = "";
  };

  const remove = (index) => onChange((proofs || []).filter((_, i) => i !== index));

  const download = (proof) => {
    const target = downloadTarget(proof);
    if (!target?.url) return;
    const anchor = document.createElement("a");
    anchor.href = target.url;
    anchor.download = proof.name || "proof";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    if (target.temporary) URL.revokeObjectURL(target.url);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!readOnly ? (
        // `relative` is load-bearing. The file input is visually hidden with
        // sr-only, which is `position: absolute`, so it resolves against the
        // nearest POSITIONED ancestor — and with none, that is the document,
        // not the scrolling <main> it appears to sit in. The browser focuses
        // the input when the file dialog closes and scrolls it into view, and
        // the panel jumps to wherever the document thinks that input is. With
        // the label positioned, the input is pinned to the button and the
        // scroll-into-view lands where the user already is.
        <label
          className={cn(
            "relative inline-flex items-center gap-2 h-10 px-4 rounded-lg border bg-white text-sm font-semibold text-foreground hover:bg-gray-50 transition",
            borderFor(error),
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          )}
        >
          <Paperclip className="h-4 w-4" />
          {t("conveyanceClaim.attachProof")}
          <input
            type="file"
            multiple={multiple}
            accept={accept}
            disabled={disabled}
            onChange={add}
            className="sr-only"
          />
        </label>
      ) : null}

      {(proofs || []).map((proof, index) => (
        <span
          key={`${proof.name || "proof"}-${index}`}
          className="inline-flex items-center gap-1 h-8 pl-1.5 pr-1.5 rounded-lg bg-workspace/50 text-[11px] font-semibold text-foreground max-w-[240px]"
        >
          {/* The name IS the download: one click on the chip saves the file,
              which is the only way a stored proof can be opened from a form. */}
          <button
            type="button"
            onClick={() => download(proof)}
            title={t("conveyanceClaim.downloadProof", { name: proof.name || "proof" })}
            className="inline-flex min-w-0 items-center gap-1.5 h-6 px-1.5 rounded text-foreground hover:text-primary transition cursor-pointer"
          >
            <Download className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{proof.name || t("conveyanceClaim.proofs")}</span>
          </button>

          {!readOnly ? (
            <button
              type="button"
              onClick={() => remove(index)}
              disabled={disabled}
              aria-label={t("formActions.remove")}
              className="h-5 w-5 grid place-items-center rounded per-file-hint hover:text-error transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </span>
      ))}

      {readOnly && (proofs || []).length === 0 ? (
        <span className="text-[12px] per-file-hint">
          {t("conveyanceClaim.noProofsAttached")}
        </span>
      ) : null}
    </div>
  );
}

export default ProofAttachments;
